import { DateTime } from 'luxon';

export const formatDateTime = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat('M/d/yyyy h:mm a ZZZZ') : '-';
};

const SALE_TIME_ZONE = 'America/Chicago';

const NBSP = '\u00a0';

// The local time carries its own date when that differs from CT's: a viewer far east of CT
// would otherwise read it against CT's date, a day off. 'numeric' writes the date as
// formatDateTime does, for a stat card beside the access cut-off's, and keeps the time and "CT"
// together so a narrow card wraps after the date.
export const centralTimeParts = (
  iso: string,
  dateStyle: 'long' | 'numeric' = 'long',
): { central: string; local: string } | undefined => {
  const local = DateTime.fromISO(iso);
  if (!local.isValid) {
    return undefined;
  }
  const central = local.setZone(SALE_TIME_ZONE);
  const localFormat =
    local.toISODate() === central.toISODate() ? 'h:mm a ZZZZ' : 'LLL d, h:mm a ZZZZ';
  return {
    central:
      dateStyle === 'numeric'
        ? `${central.toFormat('M/d/yyyy')} ${central.toFormat('h:mm a').replace(' ', NBSP)}${NBSP}CT`
        : `${central.toFormat('LLL d, yyyy, h:mm a')} CT`,
    local: local.toFormat(localFormat),
  };
};

export const formatCentralTime = (iso: string): string => {
  const parts = centralTimeParts(iso);
  return parts ? `${parts.central} (${parts.local})` : '-';
};

// Today's CT time alone, e.g. "9:41 PM CT"; another day's carries its date, "Oct 17, 9:41 PM CT".
export const formatCentralClockTime = (iso: string): string => {
  const time = DateTime.fromISO(iso).setZone(SALE_TIME_ZONE);
  if (!time.isValid) {
    return '-';
  }
  const today = DateTime.now().setZone(SALE_TIME_ZONE).toISODate();
  return `${time.toFormat(time.toISODate() === today ? 'h:mm a' : 'LLL d, h:mm a')} CT`;
};

// The calculation date is a date, stored as midnight UTC: read in a local zone west of UTC it
// would show the day before.
export const formatCalculationDate = (iso: string): string => {
  const dt = DateTime.fromISO(iso, { zone: 'utc' });
  return dt.isValid ? dt.toFormat('M/d/yyyy') : '-';
};

// api emits rounds in ascending order (roundsInOrder, loan-management SaleReads.ts), so rounds[0]
// is always round 1 -- but once round 1's deadline passes in a multi-round sale, that's a stale
// date, not "the" bid deadline. This picks whichever round is actually still upcoming, falling
// back to the last round once every deadline has passed. Deliberately not generic over the round
// shape: a call site typed `Sale | SaleAnnouncement` passes a union of two different round-array
// types (SaleRound[] | SaleAnnouncementRound[]), which a generic can't unify -- every call site
// only ever reads `round` and `deadline_at` off the result, so that's all this asks for.
export function getNextRound(
  rounds: { round: number; deadline_at: string }[],
): { round: number; deadline_at: string } | undefined {
  const now = Date.now();
  return (
    rounds.find((round) => new Date(round.deadline_at).getTime() > now) ?? rounds[rounds.length - 1]
  );
}

// Unlike getNextRound, this never falls back to a past round -- it answers "can this bidder bid
// right now", not "what's the next deadline to display". loan-management only accepts a bid (or
// the L1 bid-notice acknowledgement) for a round that hasn't closed and that this bidder's
// company was invited to; undefined means neither exists, i.e. bidding is closed for them.
export function getInvitedOpenRound(
  rounds: { round: number; deadline_at: string; invited: boolean }[],
): { round: number; deadline_at: string; invited: boolean } | undefined {
  const now = Date.now();
  return rounds.find((round) => round.invited && new Date(round.deadline_at).getTime() > now);
}

// A bidder's access ends at the sale's latest round deadline, not its access cut-off
// (loan-service SaleItem.BidderAccessEndsAtMilliseconds), so a round added later extends it.
export function getBidderAccessEndsAt(rounds: { deadline_at: string }[]): string | undefined {
  return rounds.reduce<string | undefined>(
    (latest, round) =>
      latest === undefined || new Date(round.deadline_at) > new Date(latest)
        ? round.deadline_at
        : latest,
    undefined,
  );
}

// The sale's last deadline once it has passed: every round is closed, and the bidder's access
// ended with it. undefined while any round is still open.
export function getBiddingClosedAt(rounds: { deadline_at: string }[]): string | undefined {
  const lastDeadline = getBidderAccessEndsAt(rounds);
  return lastDeadline !== undefined && new Date(lastDeadline).getTime() <= Date.now()
    ? lastDeadline
    : undefined;
}

export const formatLocalDateTime = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toLocaleString({ ...DateTime.DATETIME_MED, timeZoneName: 'short' }) : '-';
};
